import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { _isNotBlank, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { DateUtils } from '@/utils/Date.utils';

interface ArchiveStudyFormData {
  completed_at: string;
}

interface ArchiveStudyProps {
  study: Pick<Study, 'started_on'>;
  closeModal: () => void;
  onClick: (completedAt: string) => void;
  testId?: string;
}

/**
 * Confirmation modal for completing (archiving) a study. Collects a date-only `completed_at`
 * value (yyyy-MM-dd), defaulting to today and constrained so it cannot be in the future nor
 * before the study's start date.
 */
const ArchiveStudy: FC<ArchiveStudyProps> = ({ study, closeModal, onClick, testId = 'archive-study' }) => {
  const today = DateUtils.dateNow();
  const minDate = _isNotBlank(study.started_on) ? study.started_on : undefined;

  const { control, handleSubmit, formState } = useForm<ArchiveStudyFormData>({
    mode: 'onChange',
    defaultValues: { completed_at: today },
  });

  const validateCompletedAt = (value: string): true | string => {
    if (!_isNotBlank(value)) {
      return 'Please select a completion date';
    }
    if (value > today) {
      return 'Completion date cannot be in the future';
    }
    if (_isNotBlank(minDate) && value < minDate) {
      return 'Completion date cannot be before the study start date';
    }
    return true;
  };

  const onSubmit = ({ completed_at }: ArchiveStudyFormData) => {
    onClick(completed_at);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="ui__narrow-modal"
      data-test-component="ArchiveStudy"
      data-testid={testId}
    >
      <div className="pb3 tc">
        <h3 className="normal f4 pb2">Are you sure you would like to complete this study?</h3>
        <div className="f6 pb2">
          <p className="mt2 mb3">If a study is completed it will become read-only and has limited functionality.</p>
          <p>Completed studies can be resumed later in its settings page.</p>
        </div>
      </div>
      <div className="pb3">
        <label htmlFor="completed_at" className="db f6 mb2">
          Completed at
        </label>
        <Controller
          name="completed_at"
          control={control}
          rules={{ required: 'Please select a completion date', validate: validateCompletedAt }}
          render={({ field: { onChange, onBlur, value } }) => (
            <DatePickerNative
              id="completed_at"
              name="completed_at"
              value={value ?? ''}
              min={minDate}
              max={today}
              onChange={onChange}
              onBlur={onBlur}
              testId="archive-study__completed-at"
            />
          )}
        />
        {_notNil(formState.errors.completed_at) && (
          <p className="red f6 mt2 mb0" data-testid="archive-study__error">
            {formState.errors.completed_at.message}
          </p>
        )}
      </div>
      <div>
        <button
          type="submit"
          className="button w-100"
          data-testid="archive-study__submit"
          disabled={!formState.isValid || formState.isValidating || _notNil(formState.errors.completed_at)}
        >
          Complete study
        </button>
        <button
          type="button"
          className="button plain db w-100 mt2"
          onClick={closeModal}
          data-testid="archive-study__cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ArchiveStudy;
